import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const defaultNavButtonStyle = {
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  color: '#fff',
};

const defaultIndicatorStyle = {
  padding: 0,
  margin: '0 4px',
  transform: 'scale(1)',
};

function CarouselCompat(props) {
  const {
    children,
    autoPlay = true,
    interval = 5000,
    cycleNavigation = true,
    navButtonsAlwaysVisible = false,
    navButtonsProps,
    indicatorIconButtonProps,
    sx,
  } = props;

  const slides = useMemo(
    () => React.Children.toArray(children).filter(Boolean),
    [children],
  );
  const [activeIndex, setActiveIndex] = useState(0);

  const hasMultipleSlides = slides.length > 1;
  const canGoPrevious = cycleNavigation || activeIndex > 0;
  const canGoNext = cycleNavigation || activeIndex < slides.length - 1;

  const goToPrevious = useCallback(() => {
    setActiveIndex((current) => {
      if (!hasMultipleSlides) {
        return 0;
      }
      if (current === 0) {
        return cycleNavigation ? slides.length - 1 : current;
      }
      return current - 1;
    });
  }, [cycleNavigation, hasMultipleSlides, slides.length]);

  const goToNext = useCallback(() => {
    setActiveIndex((current) => {
      if (!hasMultipleSlides) {
        return 0;
      }
      if (current === slides.length - 1) {
        return cycleNavigation ? 0 : current;
      }
      return current + 1;
    });
  }, [cycleNavigation, hasMultipleSlides, slides.length]);

  useEffect(() => {
    setActiveIndex((current) => {
      if (slides.length === 0) {
        return 0;
      }
      return Math.min(current, slides.length - 1);
    });
  }, [slides.length]);

  useEffect(() => {
    if (!autoPlay || !hasMultipleSlides) {
      return undefined;
    }

    const timerId = setInterval(() => {
      goToNext();
    }, interval);

    return () => clearInterval(timerId);
  }, [autoPlay, goToNext, hasMultipleSlides, interval]);

  if (slides.length === 0) {
    return null;
  }

  const navButtonStyle = {
    ...defaultNavButtonStyle,
    ...(navButtonsProps?.style || {}),
  };
  const indicatorStyle = {
    ...defaultIndicatorStyle,
    ...(indicatorIconButtonProps?.style || {}),
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', ...sx }}>
      {hasMultipleSlides && (navButtonsAlwaysVisible || canGoPrevious) && (
        <IconButton
          aria-label="Previous slide"
          onClick={goToPrevious}
          disabled={!canGoPrevious}
          sx={{
            ...navButtonStyle,
            position: 'absolute',
            top: '50%',
            left: 8,
            transform: 'translateY(-50%)',
            zIndex: 1,
            '&:disabled': { opacity: 0.4 },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      <Box>{slides[activeIndex]}</Box>
      {hasMultipleSlides && (navButtonsAlwaysVisible || canGoNext) && (
        <IconButton
          aria-label="Next slide"
          onClick={goToNext}
          disabled={!canGoNext}
          sx={{
            ...navButtonStyle,
            position: 'absolute',
            top: '50%',
            right: 8,
            transform: 'translateY(-50%)',
            zIndex: 1,
            '&:disabled': { opacity: 0.4 },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
      {hasMultipleSlides && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 8,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          {slides.map((_, index) => (
            <IconButton
              key={index}
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => setActiveIndex(index)}
              sx={indicatorStyle}
            >
              <Box
                sx={[{
                  width: 10,
                  height: 10,
                  borderRadius: '50%'
                }, index === activeIndex ? {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                } : {
                  backgroundColor: 'rgba(255, 255, 255, 0.45)'
                }]}
              />
            </IconButton>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default CarouselCompat;
